// src/component/PriceCategoryTree.jsx
import React, { useMemo, useState, useEffect, useCallback } from "react";

/**
 * Дерево категорий (аккордеон):
 * - Только один «путь» раскрыт одновременно (корень + путь к активной).
 * - Клик по РОДИТЕЛЮ -> только раскрываем/схлопываем (запросов к API нет).
 * - Клик по ЛИСТУ -> раскрываем путь и зовем onCategoryClick(cat.id).
 */
export default function CategoryTree({ categories, onCategoryClick, activeCategory }) {
  // parent_id -> children[]
  const childrenMap = useMemo(() => {
    const map = new Map();
    for (const c of categories) {
      const pid = c.parent_id ?? null;
      if (!map.has(pid)) map.set(pid, []);
      map.get(pid).push(c);
    }
    for (const [, arr] of map) arr.sort((a, b) => a.name.localeCompare(b.name));
    return map;
  }, [categories]);

  // id -> parent_id
  const parentOf = useMemo(
    () => new Map(categories.map((c) => [c.id, c.parent_id ?? null])),
    [categories]
  );

  // Множество раскрытых узлов. По умолчанию открыт корень (null).
  const [expanded, setExpanded] = useState(() => new Set([null]));

  const getAncestors = useCallback(
    (id) => {
      const path = [];
      let cur = id;
      while (cur !== null && parentOf.has(cur)) {
        const p = parentOf.get(cur);
        path.push(p);
        cur = p;
      }
      return path;
    },
    [parentOf]
  );

  const setExpandedToPath = useCallback(
    (id) => setExpanded(new Set([null, id, ...getAncestors(id)])),
    [getAncestors]
  );

  const collectDescendants = useCallback(
    (id, acc = new Set()) => {
      const kids = childrenMap.get(id) || [];
      for (const k of kids) {
        acc.add(k.id);
        collectDescendants(k.id, acc);
      }
      return acc;
    },
    [childrenMap]
  );

  // При смене activeCategory раскрываем путь к ней
  useEffect(() => {
    if (activeCategory) setExpandedToPath(activeCategory);
  }, [activeCategory, setExpandedToPath]);

  const handleLabelClick = useCallback(
    (cat) => {
      const hasChildren = (childrenMap.get(cat.id) || []).length > 0;
      if (hasChildren) {
        // Родитель: просто управляем раскрытием
        setExpanded((prev) => {
          if (prev.has(cat.id)) {
            // Схлопываем всю ветку
            const toRemove = collectDescendants(cat.id);
            toRemove.add(cat.id);
            const next = new Set([...prev].filter((x) => !toRemove.has(x)));
            if (!next.has(null)) next.add(null);
            return next;
          } else {
            // Раскрываем только путь к этому узлу
            return new Set([null, cat.id, ...getAncestors(cat.id)]);
          }
        });
      } else {
        // Лист: раскрываем путь и дергаем загрузку товаров
        setExpandedToPath(cat.id);
        onCategoryClick(cat.id);
      }
    },
    [childrenMap, collectDescendants, getAncestors, setExpandedToPath, onCategoryClick]
  );

  const renderLevel = useCallback(
    (parentId, level = 0) => {
      const items = childrenMap.get(parentId) || [];
      if (!items.length) return null;

      return (
        <ul className={level === 0 ? "space-y-1" : "pl-3 ml-2 border-l border-orange-100 space-y-1"}>
          {items.map((cat) => {
            const hasChildren = (childrenMap.get(cat.id) || []).length > 0;
            const isOpen = expanded.has(cat.id);
            const isActive = activeCategory === cat.id;

            return (
              <li key={cat.id}>
                <div className="flex items-center">
                  {/* Пустой «плейсхолдер» вместо стрелок */}
                  <span className="w-4 inline-block" />
                  <button
                    onClick={() => handleLabelClick(cat)}
                    className={`flex-1 text-left px-2 py-1 rounded-md transition ${
                      isActive
                        ? "bg-orange-500 text-white"
                        : isOpen && hasChildren
                        ? "bg-orange-50 text-gray-800"
                        : "hover:bg-orange-50 text-gray-800"
                    }`}
                  >
                    {cat.name}
                  </button>
                </div>
                {hasChildren && isOpen && renderLevel(cat.id, level + 1)}
              </li>
            );
          })}
        </ul>
      );
    },
    [childrenMap, expanded, activeCategory, handleLabelClick]
  );

  return <nav>{renderLevel(null, 0)}</nav>;
}
